import { useSyncExternalStore } from 'react';
import { playbackTiming, type PlaybackSpeed, type PlaybackTiming } from './TurnPlayback.js';

/**
 * The turn-playback speed (03 §5): ×1 or ×2, remembered on this device. One
 * module-level value, because the playback script reads it at every beat
 * outside React, and the HUD toggle and the CSS both follow it — the
 * `data-playback-speed` stamp on <html> retimes every `--beat-*` animation,
 * including the fixed overlays portalled outside the game view.
 */
const STORAGE_KEY = 'cards.playbackSpeed';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function readStoredSpeed(): PlaybackSpeed {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === '2' ? 2 : 1;
  } catch {
    return 1;
  }
}

let speed: PlaybackSpeed = readStoredSpeed();
const listeners = new Set<() => void>();

function stamp(): void {
  if (typeof document === 'undefined') return;
  if (speed === 2) document.documentElement.setAttribute('data-playback-speed', '2');
  else document.documentElement.removeAttribute('data-playback-speed');
}
stamp();

export function getPlaybackSpeed(): PlaybackSpeed {
  return speed;
}

export function setPlaybackSpeed(next: PlaybackSpeed): void {
  if (next === speed) return;
  speed = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, String(next));
  } catch {
    // A blocked store only costs the memory of the choice, not the choice.
  }
  stamp();
  for (const listener of listeners) listener();
}

export function togglePlaybackSpeed(): void {
  setPlaybackSpeed(speed === 2 ? 1 : 2);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePlaybackSpeed(): PlaybackSpeed {
  return useSyncExternalStore(subscribe, getPlaybackSpeed, getPlaybackSpeed);
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(REDUCED_MOTION_QUERY)?.matches === true;
}

/**
 * The beat table playback waits on right now: the chosen speed, compressed
 * further under reduced motion (03 §6). Read at every beat, so a toggle takes
 * effect on the next one.
 */
export function currentPlaybackTiming(): PlaybackTiming {
  return playbackTiming(speed, prefersReducedMotion());
}

/** Tests only: forget the stored choice. */
export function resetPlaybackSpeedForTests(): void {
  speed = 1;
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored, nothing to forget.
  }
  stamp();
  for (const listener of listeners) listener();
}
